import React, { Suspense, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { wrapPromise } from "go-core";
import Header from "go-app/components/Header";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import FormatDate from "go-core/components/Formatters/FormatDate";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { ListData } from "go-list/list";
import { getSelectedSegmentForListConfig } from "go-list/list/services/segment-service";
import { ListConfig } from "go-list/list/services/types";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { adminApi } from "../../../../../../../../services/Api/Admin/adminApi";
import { FileLogApi } from "../../../../../../../../services/Api/Admin/adminTypes";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "file_log";
const resourceType = "file_log";

const List = ({ resource }: ListState) => {
	const { t } = useTranslation("admin");
	const segmentContextValue = React.useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	const replaceLink = (link: string) => {
		if (link.includes("http://")) {
			link.replace("http://", "https://");
		}
		return link;
	};

	let config = {
		fields: [
			{
				id: "name",
				name: t("modules.file_log.field.name.title"),
				type: "text",
				render: (data: FileLogApi) => `${data.name}`,
			},
			{
				id: "app_name",
				name: t("modules.file_log.field.app_name.title"),
				type: "text",
				render: (data: FileLogApi) => `${data.app_name}`,
			},
			{
				id: "app_version",
				name: t("common.word.app_version"),
				type: "text",
				render: (data: FileLogApi) => `${data.app_version}`,
			},
			{
				id: "terminal_uid",
				name: t("modules.file_log.field.terminal_uid.title"),
				type: "text",
				render: (data: FileLogApi) => `${data.terminal_uid}`,
			},
			{
				id: "created_at",
				name: t("lib:common.word.created_at"),
				type: "date",
				render: (data: FileLogApi) => FormatDate(data.created_at),
			},
			{
				id: "organization_name",
				name: t("lib:common.word.location"),
				type: "text",
				render: (data: FileLogApi) => `${data.organization_name}`,
			},
			{
				id: "link",
				name: t("modules.file_log.field.link.title"),
				render: (data: FileLogApi) => (
					<a href={replaceLink(data.link)} download target="_blank" rel="noreferrer">
						{data.link}
					</a>
				),
			},
		],
		actions: [],
		selectedColumns: ["name", "app_name", "app_version", "terminal_uid", "created_at", "organization_name", "link"],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
			},
		],
		selectedSegment: getSelectedSegmentForListConfig(data.segments, "all"),
		fetch: async (params: Record<string, any> = {}, sourceToken?: CancelTokenSource) => {
			params.include = "file_log";
			return adminApi.getFileLogs(params, { cancelToken: sourceToken?.token });
		},
		saveSegment: async (segment: SegmentType) => segmentContextValue.save(listName, resourceType, segment),
		exportConfig: {
			title: t("modules.file_log.header.title"),
			filename: t("modules.file_log.field.export_config.filename"),
		},
		numberOfStickyColumnsAtTheStart: 1,
		doNotUseDefaultDateRangeInFilters: true,
	} as ListConfig;
	const fields = data.fields ? data.fields : [];
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...fields] : fields,
	};

	return <ListData config={config} />;
};

const FileLogsIndexPage = () => {
	const { t } = useTranslation("admin");
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContextValue = React.useContext(SegmentContext);
	const isMobile = useWindowSize().isMobile;
	const { handleChangeTabTitle } = useBrowserTabTitle();

	useEffect(() => {
		handleChangeTabTitle(`${t("modules.file_log.header.title")} | ${t("lib:go_component.navigation.admin")}`);
		setResource(wrapPromise(segmentContextValue.get(listName, resourceType)));
	}, []);

	return (
		<div className="content">
			{!isMobile && <Header title={t("modules.file_log.header.title")} />}
			<Suspense fallback={<LoadingContainer />}>
				<List resource={resource} />
			</Suspense>
		</div>
	);
};

export default FileLogsIndexPage;
